import classNames from 'classnames';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';

import { useButtonGroup } from './ButtonGroupContext';
import spinnerStyles from './ButtonSpinner.module.css';
import styles from './IconButton.module.css';

export type IconButtonVariant = 'ghost' | 'outlined' | 'solid';
export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonShape = 'squared' | 'rounded' | 'circle';

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The icon to render (sized by the caller). */
  icon: ReactNode;
  /** Required for accessibility — icon buttons have no text. */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  shape?: IconButtonShape;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /** Renders the pressed/selected visual state. */
  isActive?: boolean;
}

const ICON_BUTTON_SIZES: ReadonlySet<string> = new Set(['sm', 'md', 'lg']);

/** Square, token-driven icon button (light/dark aware). The design-system
 *  replacement for Chakra's `IconButton`. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      variant = 'ghost',
      size,
      shape = 'circle',
      loading = false,
      isActive = false,
      className,
      type = 'button',
      disabled,
      ...rest
    },
    ref
  ) {
    const group = useButtonGroup();
    const groupSize =
      group?.size && ICON_BUTTON_SIZES.has(group.size)
        ? (group.size as IconButtonSize)
        : undefined;
    const resolvedSize = size ?? groupSize ?? 'md';
    const isDisabled = disabled ?? group?.disabled ?? false;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled || loading}
        aria-busy={loading || undefined}
        className={classNames(
          styles.iconButton,
          styles[variant],
          styles[resolvedSize],
          styles[shape],
          isActive && styles.active,
          className
        )}
        {...rest}
      >
        <span className={styles.icon} aria-hidden="true">
          {loading ? <span className={spinnerStyles.spinner} /> : icon}
        </span>
      </button>
    );
  }
);
